import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { useT } from '@/contexts/I18nContext';

interface Props {
  children: ReactNode;
  fallbackMessage?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

interface FallbackProps {
  message?: string;
  detail?: string | null;
  onRetry: () => void;
}

const Fallback: React.FC<FallbackProps> = ({ message, detail, onRetry }) => {
  const { t } = useT();
  return (
    <div className="px-4 py-6 text-center">
      <p className="mb-1 text-base font-semibold text-destructive">
        {message || t('error.boundaryGeneric')}
      </p>
      {detail && (
        <p className="mb-4 font-mono text-xs text-muted-foreground">{detail}</p>
      )}
      <Button variant="outline" size="sm" onClick={onRetry}>
        {t('error.boundaryRetry')}
      </Button>
    </div>
  );
};

class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught:', error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <Fallback
          message={this.props.fallbackMessage}
          detail={this.state.error?.message ?? null}
          onRetry={this.handleRetry}
        />
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
